"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import toast from "react-hot-toast"
import { API_ENDPOINTS } from "../../../../constants/config"
import useInventoryUiConfig from "../../../../hooks/useInventoryUiConfig"

const DEFAULT_ITEM = {
  item_name: "",
  category: "",
  vendor_id: "",
  vendor_name: "",
  purchase_date: "",
  purchase_cost: 0,
  quantity_purchased: 0,
  quantity_available: 0,
  minimum_stock_level: 0,
  reorder_level: 0,
  unit_of_measurement: "",
  storage_location: "",
  description: "",
  brand: "",
  size: "",
  custom_attributes: {},
  is_active: true,
  status: "Available",
}

const DEFAULT_PURCHASE = {
  date: new Date().toISOString().split("T")[0],
  quantity: 0,
  cost_per_unit: 0,
  vendor_id: "",
  vendor_name: "",
  brand: "",
}

const DEFAULT_ISSUE = { quantity: 0, issued_to: "", notes: "" }

const DEFAULT_ADJUSTMENT = { new_quantity: 0, type: "", reason: "", notes: "" }

const FILTER_STORAGE_KEY = "clynox_inventory_filters"

const DEFAULT_FILTER_OPTIONS = {
  category: [],
  sub_category: [],
  status: [],
  activeStatus: ["active"],
  brand: [],
  size: [],
  color: [],
}

const loadStoredFilterOptions = () => {
  try {
    const saved = sessionStorage.getItem(FILTER_STORAGE_KEY)
    if (!saved) return { ...DEFAULT_FILTER_OPTIONS }
    const parsed = JSON.parse(saved)
    return { ...DEFAULT_FILTER_OPTIONS, ...parsed }
  } catch {
    return { ...DEFAULT_FILTER_OPTIONS }
  }
}

export default function useInventoryManagement() {
  const [inventory, setInventory] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("")

  const [activeTab, setActiveTab] = useState("all")
  const [reorderAlerts, setReorderAlerts] = useState([])
  const [nonMovingItems, setNonMovingItems] = useState([])
  const [reorderTabItems, setReorderTabItems] = useState([])
  const [nonMovingTabItems, setNonMovingTabItems] = useState([])
  const [nonMovingDays, setNonMovingDays] = useState(90)

  const [filterOptions, setFilterOptions] = useState(loadStoredFilterOptions)

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [message, setMessage] = useState({ type: "", content: "" })

  const [sortColumn, setSortColumn] = useState("item_name")
  const [sortDirection, setSortDirection] = useState("asc")

  const [categories, setCategories] = useState([])
  const [units, setUnits] = useState([])
  const [sizes, setSizes] = useState([])
  const [brands, setBrands] = useState([])
  const [colors, setColors] = useState([])
  const [vendors, setVendors] = useState([])
  const [subCategories, setSubCategories] = useState([])
  const [inventoryFeatures, setInventoryFeatures] = useState({ color: false })

  const [showAddForm, setShowAddForm] = useState(false)
  const [showEditForm, setShowEditForm] = useState(false)
  const [showPurchaseForm, setShowPurchaseForm] = useState(false)
  const [showIssueForm, setShowIssueForm] = useState(false)
  const [showHoldForm, setShowHoldForm] = useState(false)
  const [showUnholdForm, setShowUnholdForm] = useState(false)
  const [showAdjustForm, setShowAdjustForm] = useState(false)

  const [selectedItem, setSelectedItem] = useState(null)

  const [newItem, setNewItem] = useState({ ...DEFAULT_ITEM })
  const [editItem, setEditItem] = useState({ ...DEFAULT_ITEM })
  const [newPurchase, setNewPurchase] = useState({ ...DEFAULT_PURCHASE })
  const [newIssue, setNewIssue] = useState({ ...DEFAULT_ISSUE })
  const [issueLedger, setIssueLedger] = useState(false)
  const [issueLedgerData, setIssueLedgerData] = useState({ customer_id: "", total_amount: "", paid_amount: "0", due_date: "" })

  const [holdQuantity, setHoldQuantity] = useState(0)
  const [unholdQuantity, setUnholdQuantity] = useState(0)
  const [newAdjustment, setNewAdjustment] = useState({ ...DEFAULT_ADJUSTMENT })

  const [canEditInventory, setCanEditInventory] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [totalInventory, setTotalInventory] = useState(0)
  const [inventoryMeta, setInventoryMeta] = useState({
    lowStockCount: 0,
    hasBrand: false,
    hasSize: false,
    hasDescription: false,
    hasColor: false,
    brandOptions: [],
  })

  const { config: uiConfig, loading: uiConfigLoading } = useInventoryUiConfig()

  const fetchInventoryRequestId = useRef(0)

  useEffect(() => {
    const userRaw = localStorage.getItem("user")
    let parsedUser = null
    try {
      parsedUser = userRaw ? JSON.parse(userRaw) : null
    } catch {
      parsedUser = null
    }
    const perms = Array.isArray(parsedUser?.permissions) ? parsedUser.permissions : []
    const normalized = perms.map((p) => String(p || "").trim().toLowerCase())
    const roleType = String(parsedUser?.roleType || "").trim().toLowerCase()
    const hasEdit =
      normalized.includes("inventory:edit") ||
      normalized.includes("inventory:write") ||
      normalized.includes("inventory") ||
      (roleType.includes("inventory") && !normalized.includes("inventory:read"))
    setCanEditInventory(hasEdit)
  }, [])

  const canEdit = uiConfig?.permissions?.canEdit ?? canEditInventory
  const canHistory = uiConfig?.permissions?.canHistory ?? canEditInventory
  const showAddItem = uiConfig?.buttons?.showAddItem ?? canEdit
  const showPurchase = uiConfig?.buttons?.showPurchase ?? canEdit
  const showIssue = uiConfig?.buttons?.showIssue ?? canEdit
  const isColorEnabled = Boolean(inventoryFeatures?.color)
  const isSubCategoryEnabled = Boolean(inventoryFeatures?.sub_category)

  const fetchInventory = async () => {
    const requestId = ++fetchInventoryRequestId.current
    try {
      setLoading(true)
      setError(null)

      const token = localStorage.getItem("token")
      if (!token) throw new Error("Authentication token not found")

      const params = new URLSearchParams()
      if (filterOptions.activeStatus.length) params.append("activeStatus", filterOptions.activeStatus.join(","))
      if (debouncedSearchTerm) params.append("search", debouncedSearchTerm)
      if (filterOptions.category.length) params.append("category", filterOptions.category.join(","))
      if (filterOptions.sub_category.length) params.append("sub_category", filterOptions.sub_category.join(","))
      if (filterOptions.status.length) params.append("status", filterOptions.status.join(","))
      if (filterOptions.brand.length) params.append("brand", filterOptions.brand.join(","))
      if (filterOptions.size.length) params.append("size", filterOptions.size.join(","))
      if (filterOptions.color.length) params.append("color", filterOptions.color.join(","))
      params.append("page", currentPage)
      params.append("limit", itemsPerPage)
      params.append("paginate", "true")
      params.append("sortBy", sortColumn)
      params.append("sortOrder", sortDirection)

      const response = await fetch(`${API_ENDPOINTS.inventory}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      })

      if (!response.ok) throw new Error(`Failed to fetch inventory: ${response.statusText}`)

      const data = await response.json()

      // A newer request already landed while this one was in flight — drop this
      // stale response so it can't clobber inventoryMeta.brandOptions with an
      // unscoped/older filter's result.
      if (requestId !== fetchInventoryRequestId.current) return

      const items = data.data || []
      setInventory(items)
      setTotalInventory(typeof data.count === "number" ? data.count : items.length)
      setInventoryMeta(data.meta || { lowStockCount: 0, hasBrand: false, hasSize: false, hasDescription: false, hasColor: false, brandOptions: [] })
    } catch (err) {
      if (requestId !== fetchInventoryRequestId.current) return
      console.error("Error fetching inventory:", err)
      setError(err.message)
    } finally {
      if (requestId === fetchInventoryRequestId.current) setLoading(false)
    }
  }

  const fetchMetadata = async () => {
    const token = localStorage.getItem("token")
    if (!token) return
    const headers = { Authorization: `Bearer ${token}` }
    const safeJson = (r) => (r.ok ? r.json() : Promise.resolve(null))

    await Promise.all([
      fetch(API_ENDPOINTS.inventoryFeatureConfig, { headers }).then(safeJson).then((d) => setInventoryFeatures(d?.data?.features ?? { color: false })).catch(() => setInventoryFeatures({ color: false })),
      fetch(API_ENDPOINTS.inventoryCategories, { headers }).then(safeJson).then((d) => d && setCategories(d.data || [])).catch(() => {}),
      fetch(API_ENDPOINTS.inventoryUnits, { headers }).then(safeJson).then((d) => d && setUnits(d.data || [])).catch(() => {}),
      fetch(API_ENDPOINTS.inventorySizes, { headers }).then(safeJson).then((d) => d && setSizes(d.data || [])).catch(() => {}),
      fetch(API_ENDPOINTS.inventoryBrands, { headers }).then(safeJson).then((d) => d && setBrands(d.data || [])).catch(() => {}),
      ...(canEdit || showPurchase || showIssue ? [fetch(API_ENDPOINTS.vendors, { headers }).then(safeJson).then((d) => d && setVendors(d.data || [])).catch(() => {})] : []),
    ])
  }

  const fetchColors = async () => {
    try {
      const token = localStorage.getItem("token")
      if (!token) throw new Error("Authentication token not found")
      const response = await fetch(API_ENDPOINTS.inventoryColors, { headers: { Authorization: `Bearer ${token}` } })
      if (!response.ok) throw new Error(`Failed to fetch colors: ${response.statusText}`)
      const data = await response.json()
      setColors(data.data || [])
    } catch (err) {
      console.error("Error fetching colors:", err)
      toast.error(err.message || "Failed to load colors")
    }
  }


  const fetchSubCategories = async () => {
    try {
      const token = localStorage.getItem("token")
      if (!token) throw new Error("Authentication token not found")
      const response = await fetch(API_ENDPOINTS.inventorySubCategories, { headers: { Authorization: `Bearer ${token}` } })
      if (!response.ok) throw new Error(`Failed to fetch sub-categories: ${response.statusText}`)
      const data = await response.json()
      setSubCategories(data.data || [])
    } catch (err) {
      console.error("Error fetching sub-categories:", err)
    }
  }

  const fetchReorderAlerts = async () => {
    try {
      const token = localStorage.getItem("token")
      if (!token) return
      const response = await fetch(API_ENDPOINTS.inventoryReorderAlerts, { headers: { Authorization: `Bearer ${token}` } })
      if (!response.ok) return
      const data = await response.json()
      setReorderAlerts(data.data || [])
    } catch (err) {
      console.error("Error fetching reorder alerts:", err)
    }
  }

  const fetchNonMovingItems = async () => {
    try {
      const token = localStorage.getItem("token")
      if (!token) return
      const response = await fetch(`${API_ENDPOINTS.inventoryNonMoving}?days=${nonMovingDays}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) return
      const data = await response.json()
      setNonMovingItems(data.data || [])
    } catch (err) {
      console.error("Error fetching non-moving items:", err)
    }
  }

  const fetchReorderTabData = async () => {
    try {
      const token = localStorage.getItem("token")
      if (!token) return
      const params = new URLSearchParams()
      if (debouncedSearchTerm) params.append("search", debouncedSearchTerm)
      const response = await fetch(`${API_ENDPOINTS.inventoryReorderAlerts}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) return
      const data = await response.json()
      setReorderTabItems(data.data || [])
    } catch (err) {
      console.error("Error fetching reorder tab data:", err)
    }
  }

  const fetchNonMovingTabData = async () => {
    try {
      const token = localStorage.getItem("token")
      if (!token) return
      const params = new URLSearchParams()
      params.append("days", nonMovingDays)
      if (debouncedSearchTerm) params.append("search", debouncedSearchTerm)
      const response = await fetch(`${API_ENDPOINTS.inventoryNonMoving}?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) return
      const data = await response.json()
      setNonMovingTabItems(data.data || [])
    } catch (err) {
      console.error("Error fetching non-moving tab data:", err)
    }
  }

  useEffect(() => {
    fetchMetadata()
  }, [canEdit, showPurchase, showIssue])

  useEffect(() => {
    if (isColorEnabled) fetchColors()
    else setColors([])
  }, [isColorEnabled])

  useEffect(() => {
    if (isSubCategoryEnabled) fetchSubCategories()
    else setSubCategories([])
  }, [isSubCategoryEnabled])

  useEffect(() => {
    setFilterOptions((prev) =>
      prev.sub_category.length === 0 && prev.brand.length === 0 ? prev : { ...prev, sub_category: [], brand: [] },
    )
  }, [filterOptions.category])

  useEffect(() => {
    try {
      sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filterOptions))
    } catch {
     
    }
  }, [filterOptions])

  const clearFilters = () => setFilterOptions({ ...DEFAULT_FILTER_OPTIONS })

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 350)
    return () => clearTimeout(timeoutId)
  }, [searchTerm])

  useEffect(() => {
    fetchInventory()
  }, [filterOptions, debouncedSearchTerm, currentPage, itemsPerPage, sortColumn, sortDirection])

  useEffect(() => {
    if (activeTab === "reorder") {
      fetchReorderAlerts()
      fetchReorderTabData()
    }
    if (activeTab === "nonmoving") {
      fetchNonMovingItems()
      fetchNonMovingTabData()
    }
  }, [activeTab, nonMovingDays, debouncedSearchTerm])

  useEffect(() => {
    setCurrentPage(1)
  }, [activeTab, debouncedSearchTerm, filterOptions, sortColumn, sortDirection])

  
  useEffect(() => {
    fetchReorderAlerts()
    fetchNonMovingItems()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

 
  const DESC_FIRST_COLUMNS = new Set(["inventory_id", "quantity_available", "last_stock_update"])

  const handleInventorySort = (column) => {
    const newDirection =
      sortColumn === column
        ? sortDirection === "asc" ? "desc" : "asc"
        : DESC_FIRST_COLUMNS.has(column) ? "desc" : "asc"
    setSortColumn(column)
    setSortDirection(newDirection)
  }

  const openAddForm = () => setShowAddForm(true)

  const openEditForm = (item) => {
    setSelectedItem(item)
    const formattedItem = {
      ...item,
      custom_attributes: item.custom_attributes || {},
      purchase_date: item.purchase_date ? new Date(item.purchase_date).toISOString().split("T")[0] : "",
      is_active: item.is_active !== false,
    }
    setEditItem(formattedItem)
    setShowEditForm(true)
  }

  const openPurchaseForm = (item) => {
    setSelectedItem(item)
    setShowPurchaseForm(true)
  }

  const openIssueForm = (item) => {
    setSelectedItem(item)
    setNewIssue({ ...DEFAULT_ISSUE })
    setIssueLedger(false)
    setIssueLedgerData({ customer_id: "", total_amount: "", paid_amount: "0", due_date: "" })
    setShowIssueForm(true)
  }

  const handleAddItem = async () => {
    if (!newItem.item_name || !newItem.category) {
      setMessage({ type: "error", content: "Please fill all required fields" })
      return
    }

    if (!newItem.vendor_id && !newItem.brand) {
      setMessage({ type: "error", content: "Please select either a vendor or a brand" })
      return
    }

    const payload = { ...newItem }
    if (!payload.custom_attributes) payload.custom_attributes = {}
    if (isColorEnabled) {
      const selectedColor = payload.custom_attributes?.color
      payload.custom_attributes = selectedColor ? { ...payload.custom_attributes, color: selectedColor } : payload.custom_attributes
    }
    if (!payload.vendor_id || payload.vendor_id === "placeholder") {
      delete payload.vendor_id
      delete payload.vendor_name
    }

    try {
      setLoading(true)
      const token = localStorage.getItem("token")
      if (!token) throw new Error("Authentication token not found")

      const response = await fetch(API_ENDPOINTS.inventory, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      })

      if (!response.ok) throw new Error(`Failed to add inventory item: ${response.statusText}`)

      await response.json()
      setShowAddForm(false)
      setCurrentPage(1)
      fetchInventory()
      fetchReorderAlerts()
      fetchNonMovingItems()
      fetchReorderTabData()
      fetchNonMovingTabData()
      setNewItem({ ...DEFAULT_ITEM })

      toast.success("Item added successfully.")
      setMessage({ type: "success", content: "Item added successfully!" })
      setTimeout(() => setMessage({ type: "", content: "" }), 3000)
    } catch (err) {
      console.error("Error adding inventory item:", err)
      toast.error(err.message)
      setMessage({ type: "error", content: err.message })
      setTimeout(() => setMessage({ type: "", content: "" }), 3000)
    } finally {
      setLoading(false)
    }
  }

  const handleEditItem = async () => {
    if (!editItem.item_name || !editItem.category) {
      setMessage({ type: "error", content: "Please fill all required fields" })
      return
    }

    if (!editItem.vendor_id && !editItem.brand) {
      setMessage({ type: "error", content: "Please select either a vendor or a brand" })
      return
    }

    const payload = { ...editItem }
    if (!payload.custom_attributes) payload.custom_attributes = {}
    if (isColorEnabled) {
      const selectedColor = payload.custom_attributes?.color
      payload.custom_attributes = selectedColor ? { ...payload.custom_attributes, color: selectedColor } : payload.custom_attributes
    }
    delete payload.quantity_available
    delete payload.quantity_purchased
    delete payload.on_hold_quantity
    if (!payload.vendor_id || payload.vendor_id === "placeholder") {
      delete payload.vendor_id
      delete payload.vendor_name
    }

    try {
      setLoading(true)
      const token = localStorage.getItem("token")
      if (!token) throw new Error("Authentication token not found")

      const response = await fetch(API_ENDPOINTS.inventoryItem(selectedItem.inventory_id), {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      })

      if (!response.ok) throw new Error(`Failed to update inventory item: ${response.statusText}`)

      await response.json()
      setShowEditForm(false)
      setSelectedItem(null)
      fetchInventory()
      fetchReorderAlerts()
      fetchNonMovingItems()
      fetchReorderTabData()
      fetchNonMovingTabData()

      toast.success("Item updated successfully.")
      setMessage({ type: "success", content: "Item updated successfully!" })
      setTimeout(() => setMessage({ type: "", content: "" }), 3000)
    } catch (err) {
      console.error("Error updating inventory item:", err)
      toast.error(err.message)
      setMessage({ type: "error", content: err.message })
      setTimeout(() => setMessage({ type: "", content: "" }), 3000)
    } finally {
      setLoading(false)
    }
  }

  const handleIssueStock = async (ledgerOverride = null) => {
    if (!selectedItem) {
      setMessage({ type: "error", content: "Please select an item first" })
      return
    }
    const qty = Number(newIssue.quantity) || 0
    if (qty <= 0) {
      setMessage({ type: "error", content: "Quantity must be greater than 0" })
      return
    }
    if (selectedItem.quantity_available < qty) {
      setMessage({ type: "error", content: "Insufficient stock" })
      return
    }
    // Use override ledger data if provided (from new dialog UI), else fall back to hook state
    const useLedger = ledgerOverride !== null ? ledgerOverride.enabled : issueLedger
    const useLedgerData = ledgerOverride !== null ? ledgerOverride.data : issueLedgerData

    if (useLedger) {
      if (!useLedgerData.customer_id) {
        setMessage({ type: "error", content: "Please select a party for ledger tracking" })
        return
      }
      if (!useLedgerData.total_amount || useLedgerData.total_amount === "") {
        setMessage({ type: "error", content: "Please enter total amount for ledger tracking" })
        return
      }
    }
    try {
      setLoading(true)
      const token = localStorage.getItem("token")
      if (!token) throw new Error("Authentication token not found")
      const response = await fetch(API_ENDPOINTS.inventoryIssue(selectedItem.inventory_id), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          quantity: qty,
          issued_to: newIssue.issued_to || "",
          notes: newIssue.notes || "",
          // Party identity is independent of ledger/amount tracking — attach it
          // whenever a real party was picked, even if ledger tracking is off.
          ...(useLedgerData.customer_id
            ? { customer_id: useLedgerData.customer_id, customer_name: useLedgerData.customer_name || "" }
            : {}),
          ...(useLedger && useLedgerData.customer_id && useLedgerData.total_amount !== ""
            ? {
                total_amount: parseFloat(useLedgerData.total_amount) || 0,
                paid_amount: parseFloat(useLedgerData.paid_amount) || 0,
                due_date: useLedgerData.due_date || null,
              }
            : {}),
        }),
      })
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}))
        throw new Error(errData.message || "Failed to issue stock")
      }
      const resData = await response.json()
      setShowIssueForm(false)
      setNewIssue({ ...DEFAULT_ISSUE })
      setIssueLedger(false)
      setIssueLedgerData({ customer_id: "", total_amount: "", paid_amount: "0", due_date: "" })
      setSelectedItem(null)
      fetchInventory()
      fetchReorderAlerts()
      fetchNonMovingItems()
      fetchReorderTabData()
      fetchNonMovingTabData()
      if (resData.ledger_warning) {
        toast.warning(`Stock issued but ledger not updated: ${resData.ledger_warning}`)
        setMessage({ type: "error", content: `Stock issued but ledger not updated: ${resData.ledger_warning}` })
      } else {
        toast.success("Stock issued successfully.")
        setMessage({ type: "success", content: "Stock issued successfully" })
      }
      setTimeout(() => setMessage({ type: "", content: "" }), 3000)
    } catch (err) {
      toast.error(err.message || "Failed to issue stock")
      setMessage({ type: "error", content: err.message || "Failed to issue stock" })
      setTimeout(() => setMessage({ type: "", content: "" }), 3000)
    } finally {
      setLoading(false)
    }
  }

  const handleAddPurchase = async (purchaseOverride = null) => {
    if (!selectedItem) {
      setMessage({ type: "error", content: "Please select an item first" })
      return
    }

    const today = new Date().toISOString().split('T')[0]
    if (newPurchase.date && newPurchase.date > today) {
      setMessage({ type: "error", content: "Purchase date cannot be in the future" })
      return
    }

    try {
      setLoading(true)
      const token = localStorage.getItem("token")
      if (!token) throw new Error("Authentication token not found")

      const brandRows = purchaseOverride?.brandRows
      const rows = brandRows?.filter(r => (parseFloat(r.quantity) || 0) > 0) || []

      // Build base payload with vendor info
      const buildBase = () => {
        const base = { ...newPurchase }
        if (purchaseOverride?.vendor?.id) {
          base.vendor_id = purchaseOverride.vendor.id
          base.vendor_name = purchaseOverride.vendor.name
        } else if (purchaseOverride?.vendor?.name) {
          delete base.vendor_id
          base.vendor_name = purchaseOverride.vendor.name
        } else {
          delete base.vendor_id
          delete base.vendor_name
        }
        return base
      }

      if (rows.length > 0) {
        // Multi-brand: one API call per row, payment split proportionally
        const totalSubtotal = rows.reduce((s, r) => s + (parseFloat(r.quantity) || 0) * (parseFloat(r.cost_per_unit) || 0), 0)
        const payTotal = purchaseOverride?.payment?.total_amount || 0
        const payPaid = purchaseOverride?.payment?.paid_amount || 0

        for (const row of rows) {
          const rowSubtotal = (parseFloat(row.quantity) || 0) * (parseFloat(row.cost_per_unit) || 0)
          const ratio = totalSubtotal > 0 ? rowSubtotal / totalSubtotal : 1 / rows.length
          const payload = {
            ...buildBase(),
            quantity: parseFloat(row.quantity) || 0,
            cost_per_unit: parseFloat(row.cost_per_unit) || 0,
            brand: row.brand,
          }
          if (purchaseOverride?.payment) {
            payload.total_amount = Math.round(ratio * payTotal * 100) / 100
            payload.paid_amount = Math.round(ratio * payPaid * 100) / 100
            if (purchaseOverride.payment.due_date) payload.due_date = purchaseOverride.payment.due_date
          }
          const res = await fetch(API_ENDPOINTS.inventoryPurchase(selectedItem.inventory_id), {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify(payload),
          })
          if (!res.ok) {
            const errData = await res.json().catch(() => ({}))
            throw new Error(errData.message || (row.brand ? `Failed to add purchase for brand "${row.brand}"` : "Failed to add purchase record"))
          }
        }

        toast.success(`${rows.length} purchase record${rows.length > 1 ? "s" : ""} added successfully.`)
        setMessage({ type: "success", content: `${rows.length} purchase record${rows.length > 1 ? "s" : ""} added!` })
      } else {
        // Fallback: original single-row flow (no brandRows provided)
        const payload = buildBase()
        if (purchaseOverride?.payment) {
          payload.total_amount = purchaseOverride.payment.total_amount
          payload.paid_amount = purchaseOverride.payment.paid_amount
          payload.payment_status = purchaseOverride.payment.status
          if (purchaseOverride.payment.due_date) payload.due_date = purchaseOverride.payment.due_date
        }
        const response = await fetch(API_ENDPOINTS.inventoryPurchase(selectedItem.inventory_id), {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify(payload),
        })
        if (!response.ok) throw new Error(`Failed to add purchase record: ${response.statusText}`)
        toast.success("Purchase record added successfully.")
        setMessage({ type: "success", content: "Purchase record added successfully!" })
      }

      setShowPurchaseForm(false)
      fetchInventory()
      fetchReorderAlerts()
      fetchNonMovingItems()
      fetchReorderTabData()
      fetchNonMovingTabData()
      setNewPurchase({ ...DEFAULT_PURCHASE })
      setTimeout(() => setMessage({ type: "", content: "" }), 3000)
    } catch (err) {
      console.error("Error adding purchase record:", err)
      toast.error(err.message)
      setMessage({ type: "error", content: err.message })
      setTimeout(() => setMessage({ type: "", content: "" }), 3000)
    } finally {
      setLoading(false)
    }
  }

  const handleAdjustStock = async () => {
    if (!selectedItem) {
      setMessage({ type: "error", content: "Please select an item first" })
      return
    }
    const newQty = Number(newAdjustment.new_quantity)
    if (!Number.isFinite(newQty) || newQty < 0) {
      setMessage({ type: "error", content: "New quantity must be a non-negative number" })
      return
    }
    try {
      setLoading(true)
      const token = localStorage.getItem("token")
      if (!token) throw new Error("Authentication token not found")
      const adjustEndpoint =
        typeof API_ENDPOINTS.inventoryAdjust === "function"
          ? API_ENDPOINTS.inventoryAdjust(selectedItem.inventory_id)
          : `${API_ENDPOINTS.inventory}/${selectedItem.inventory_id}/adjust`
      const response = await fetch(adjustEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(newAdjustment),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.message || "Failed to adjust stock")
      setShowAdjustForm(false)
      setNewAdjustment({ ...DEFAULT_ADJUSTMENT })
      setSelectedItem(null)
      fetchInventory()
      fetchReorderAlerts()
      fetchNonMovingItems()
      fetchReorderTabData()
      fetchNonMovingTabData()
      toast.success("Stock adjusted successfully.")
      setMessage({ type: "success", content: "Stock adjusted successfully" })
      setTimeout(() => setMessage({ type: "", content: "" }), 3000)
    } catch (err) {
      toast.error(err.message || "Failed to adjust stock")
      setMessage({ type: "error", content: err.message || "Failed to adjust stock" })
      setTimeout(() => setMessage({ type: "", content: "" }), 3000)
    } finally {
      setLoading(false)
    }
  }

  const handleUnholdStock = async () => {
    if (!selectedItem) {
      setMessage({ type: "error", content: "Please select an item first" })
      return
    }
    const qty = Number(unholdQuantity) || 0
    if (qty <= 0) {
      setMessage({ type: "error", content: "Quantity must be greater than 0" })
      return
    }
    const currentOnHold = Number(selectedItem.on_hold_quantity) || 0
    if (qty > currentOnHold) {
      setMessage({ type: "error", content: "Quantity cannot exceed on-hold stock" })
      return
    }
    try {
      setLoading(true)
      const token = localStorage.getItem("token")
      if (!token) throw new Error("Authentication token not found")
      const unholdEndpoint =
        typeof API_ENDPOINTS.inventoryUnhold === "function"
          ? API_ENDPOINTS.inventoryUnhold(selectedItem.inventory_id)
          : `${API_ENDPOINTS.inventory}/${selectedItem.inventory_id}/unhold`
      const response = await fetch(unholdEndpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ quantity: qty }),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.message || "Failed to release hold stock")
      setShowUnholdForm(false)
      setUnholdQuantity(0)
      setSelectedItem(null)
      fetchInventory()
      fetchReorderAlerts()
      fetchNonMovingItems()
      fetchReorderTabData()
      fetchNonMovingTabData()
      toast.success("Hold stock released successfully.")
      setMessage({ type: "success", content: "Hold stock released successfully" })
      setTimeout(() => setMessage({ type: "", content: "" }), 3000)
    } catch (err) {
      toast.error(err.message || "Failed to release hold stock")
      setMessage({ type: "error", content: err.message || "Failed to release hold stock" })
      setTimeout(() => setMessage({ type: "", content: "" }), 3000)
    } finally {
      setLoading(false)
    }
  }

  const handleHoldStock = async () => {
    if (!selectedItem) return
    const qty = Number.parseFloat(holdQuantity) || 0
    if (qty <= 0) {
      setMessage({ type: "error", content: "Quantity must be greater than 0" })
      return
    }
    try {
      setLoading(true)
      const token = localStorage.getItem("token")
      if (!token) throw new Error("Authentication token not found")
      const response = await fetch(API_ENDPOINTS.inventoryHold(selectedItem.inventory_id), {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ quantity: qty }),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.message || "Failed to place stock on hold")
      setShowHoldForm(false)
      setHoldQuantity(0)
      setSelectedItem(null)
      fetchInventory()
      fetchReorderAlerts()
      fetchNonMovingItems()
      fetchReorderTabData()
      fetchNonMovingTabData()
      toast.success("Stock placed on hold")
    } catch (err) {
      toast.error(err.message || "Failed to place stock on hold")
    } finally {
      setLoading(false)
    }
  }

  const matchesArrayFilter = (selectedValues, itemValue) =>
    selectedValues.length === 0 || selectedValues.includes(itemValue)

  const tableData = useMemo(() => {
    if (activeTab === "reorder") {
      return reorderTabItems.filter((item) => {
        return (
          matchesArrayFilter(filterOptions.category, item.category) &&
          matchesArrayFilter(filterOptions.sub_category, item.sub_category) &&
          matchesArrayFilter(filterOptions.status, item.status)
        )
      })
    }
    if (activeTab === "nonmoving") {
      return nonMovingTabItems.filter((item) => {
        return (
          matchesArrayFilter(filterOptions.category, item.category) &&
          matchesArrayFilter(filterOptions.sub_category, item.sub_category) &&
          matchesArrayFilter(filterOptions.status, item.status)
        )
      })
    }
    return inventory
  }, [activeTab, filterOptions.category, filterOptions.sub_category, filterOptions.status, inventory, nonMovingTabItems, reorderTabItems])

  const currentTotal = activeTab === "all" ? totalInventory : tableData.length

  const paginatedData = useMemo(() => {
    // The "all" tab is already paginated server-side (see fetchInventory).
    // Other tabs page client-side over their own fully-fetched lists.
    if (activeTab === "all") return tableData
    return tableData.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
  }, [activeTab, currentPage, itemsPerPage, tableData])

  const lowStockCount = inventoryMeta.lowStockCount
  const hasBrand = inventoryMeta.hasBrand
  const hasSize = inventoryMeta.hasSize
  const hasDescription = inventoryMeta.hasDescription
  const brandOptions = inventoryMeta.brandOptions

  const subCategoryOptions = useMemo(() => {
    if (filterOptions.category.length === 0) return subCategories
    const selectedCategoryIds = categories
      .filter((c) => filterOptions.category.includes(c.name))
      .map((c) => String(c._id))
    return subCategories.filter((sc) => selectedCategoryIds.includes(String(sc.category)))
  }, [categories, filterOptions.category, subCategories])

  return {
    // UI config
    uiConfigLoading,
    canEdit,
    canHistory,
    showAddItem,
    showPurchase,
    showIssue,

    // Data
    inventory,
    reorderAlerts,
    nonMovingItems,
    categories,
    units,
    sizes,
    brands,
    colors,
    vendors,
    subCategories,
    isColorEnabled,
    isSubCategoryEnabled,

    // State
    searchTerm,
    setSearchTerm,
    activeTab,
    setActiveTab,
    nonMovingDays,
    setNonMovingDays,
    filterOptions,
    setFilterOptions,
    clearFilters,
    loading,
    error,
    message,
    setMessage,
    sortColumn,
    sortDirection,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    totalInventory,

    // Derived
    tableData,
    paginatedData,
    currentTotal,
    lowStockCount,
    hasBrand,
    hasSize,
    hasDescription,
    brandOptions,
    subCategoryOptions,

    // Selection + dialogs
    selectedItem,
    setSelectedItem,
    showAddForm,
    setShowAddForm,
    showEditForm,
    setShowEditForm,
    showPurchaseForm,
    setShowPurchaseForm,
    showIssueForm,
    setShowIssueForm,
    showHoldForm,
    setShowHoldForm,
    showUnholdForm,
    setShowUnholdForm,
    showAdjustForm,
    setShowAdjustForm,

    // Form state
    newItem,
    setNewItem,
    editItem,
    setEditItem,
    newPurchase,
    setNewPurchase,
    newIssue,
    setNewIssue,
    issueLedger,
    setIssueLedger,
    issueLedgerData,
    setIssueLedgerData,
    holdQuantity,
    setHoldQuantity,
    unholdQuantity,
    setUnholdQuantity,
    newAdjustment,
    setNewAdjustment,

    // Actions
    fetchInventory,
    handleInventorySort,
    openAddForm,
    openEditForm,
    openPurchaseForm,
    openIssueForm,
    handleAddItem,
    handleEditItem,
    handleAddPurchase,
    handleIssueStock,
    handleAdjustStock,
    handleUnholdStock,
    handleHoldStock,
  }
}

